import React, { useState, useEffect, useCallback, useRef } from 'react';
import LeftPane from '../Shared/LeftPane';
import RightPane from '../Shared/RightPane';
import FileUpload from '../Shared/FileUpload';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { COMPRESS_IMAGE } from '../Common/Constants/ComponentType';
import compressImages from './api';

const ImageCompressor = () => {
    const [files, setFiles] = useState([]);
    const [compressionLevel, setCompressionLevel] = useState(40);
    const [processingText, setProcessingText] = useState('Compress Images');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [isOptionsVisible, setIsOptionsVisible] = useState(false);
    const fileInputRef = useRef(null);

    const generateId = () =>
        Date.now().toString() + Math.random().toString(36).slice(2);

    // Lock body scroll on mobile options
    useEffect(() => {
        document.body.style.overflow = isOptionsVisible ? 'hidden' : 'auto';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isOptionsVisible]);

    /** Handle file selection */
    const handleFiles = useCallback((acceptedFiles) => {
        setError('');
        if (!acceptedFiles || acceptedFiles.length === 0) {
            setError("Please select at least one image.");
            return;
        }

        const newFiles = Array.from(acceptedFiles).map((file) => ({
            id: generateId(),
            file,
            name: file.name,
            lastModified: file.lastModified,
            preview: URL.createObjectURL(file),
            rotation: 0, // Initial rotation
        }));

        setFiles((prev) => [...prev, ...newFiles]);
    }, []);

    /** Rotate image before compression */
    const handleRotate = (id) => {
    setFiles(prevFiles => {        
        const index = prevFiles.findIndex(f => f.id === id);
        if (index === -1) return prevFiles; // no file found

        const fileData = prevFiles[index];
        const img = new Image();
        img.src = fileData.preview;

        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            const angle = ((fileData.rotation || 0) + 90) % 360;
            const radians = (angle * Math.PI) / 180;

            if (angle === 90 || angle === 270) {
                canvas.width = img.height;
                canvas.height = img.width;
            } else {
                canvas.width = img.width;
                canvas.height = img.height;
            }

            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate(radians);
            ctx.drawImage(img, -img.width / 2, -img.height / 2);

            canvas.toBlob((blob) => {
                if (!blob) return;
                const rotatedFile = new File([blob], fileData.name, { type: fileData.file.type });

                setFiles(prev => {
                    const updated = [...prev];
                    updated[index] = {
                        ...updated[index],
                        file: rotatedFile,
                        rotation: angle,
                    };
                    return updated;
                });
            }, fileData.file.type);
        };

        return prevFiles; // the actual update happens inside canvas.toBlob
    });
};


    /** Remove image */
    const handleRemove = useCallback((fileId) => {
        setFiles((prev) => prev.filter((f) => f.id !== fileId));
    }, []);

    /** Compress all images */
    const handleCompress = async () => {        
        if (files.length === 0) {
            setError('Please select images first.');
            return;
        }

        setError('');
        setProcessingText('Compressing Images...');
        setLoading(true);

        await compressImages(files, compressionLevel);
        
        setLoading(false);
        setProcessingText('Compress Images');
        setError('');
    };

    if (files.length === 0) {
        return (
            <FileUpload
                applicationType={{ 'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.svg+xml', '.tiff', '.heic', '.heif', '.x-icon'] }}
                handleFiles={handleFiles}
                fileType={'Image'}
            />
        );
    }

    return (
        <div className="main-container-itp">           
            <input
                type="file"
                ref={fileInputRef}
                onChange={(e) => handleFiles(e.target.files)}
                multiple
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp,image/svg+xml,image/tiff,image/x-icon,image/heic,image/heif"
                style={{ display: 'none' }}
            />

            <LeftPane
                handleFiles={handleFiles}
                fileInputRef={fileInputRef}
                setFiles={setFiles}
                files={files}
                isGenerating={loading}
                componentType={COMPRESS_IMAGE}
                handleRemove={handleRemove}
                handleRotate={handleRotate}
                setIsOptionsVisible={setIsOptionsVisible}
            />

            <RightPane
                handleDownload={handleCompress}
                isGenerating={loading}
                processingText={processingText}
                componentType={COMPRESS_IMAGE}
                error={error}
                isOptionsVisible={isOptionsVisible}
                setCompressionLevel={setCompressionLevel}
                compressionLevel={compressionLevel}
                setIsOptionsVisible={setIsOptionsVisible}
            />
        </div>
    );
};

export default ImageCompressor;
